import {
  getStorageInfo,
  cleanupOldFiles,
  getStoredFilesInfo,
} from "./fileStorage.js";
import { cleanupUploadedFiles } from "./fileUpload.js";
import {
  clearQuotaError,
  hasRecentQuotaError,
  probeQuotaRecovered,
} from "./quotaSignal.js";

/**
 * Storage Manager - Centralized storage management for the app
 * Provides utilities for monitoring, cleaning up, and managing local file storage
 */

// Storage thresholds (in percentage)
const STORAGE_WARNING_THRESHOLD = 80;
const STORAGE_CRITICAL_THRESHOLD = 95;

export class StorageManager {
  constructor() {
    this.storageInfo = null;
    this.lastCheck = null;
  }

  // Get current storage status with warnings
  async getStorageStatus() {
    this.storageInfo = await getStorageInfo();
    this.lastCheck = new Date();

    // A write that actually failed with QuotaExceededError outranks whatever
    // estimate() reports — estimate() can look healthy while writes fail.
    // Recovery is verified symmetrically: a successful probe write clears the
    // pressure state instead of waiting out the TTL.
    if (hasRecentQuotaError()) {
      if (await probeQuotaRecovered()) {
        clearQuotaError();
      } else {
        return {
          status: "critical",
          message: "Storage writes are failing (quota exceeded)",
          ...(this.storageInfo || {}),
          usagePercent: Math.max(this.storageInfo?.usagePercent ?? 100, 100),
        };
      }
    }

    if (!this.storageInfo) {
      return { status: "unknown", message: "Storage API not supported" };
    }

    const { usagePercent } = this.storageInfo;

    if (usagePercent >= STORAGE_CRITICAL_THRESHOLD) {
      return {
        status: "critical",
        message: `Storage is ${usagePercent}% full. Please free up space.`,
        ...this.storageInfo,
      };
    } else if (usagePercent >= STORAGE_WARNING_THRESHOLD) {
      return {
        status: "warning",
        message: `Storage is ${usagePercent}% full. Consider cleaning up old files.`,
        ...this.storageInfo,
      };
    } else {
      return {
        status: "ok",
        message: `Storage usage: ${usagePercent}%`,
        ...this.storageInfo,
      };
    }
  }

  // Auto cleanup based on storage status
  async performAutoCleanup() {
    const status = await this.getStorageStatus();

    if (status.status === "critical") {
      // Aggressive cleanup - remove files older than 3 days
      const deletedCount = await cleanupOldFiles(3);
      console.log(`Critical storage cleanup: removed ${deletedCount} files`);
      return deletedCount;
    } else if (status.status === "warning") {
      // Regular cleanup - remove files older than 7 days
      const deletedCount = await cleanupOldFiles(7);
      console.log(`Warning storage cleanup: removed ${deletedCount} files`);
      return deletedCount;
    }

    return 0;
  }

  // Get detailed storage report
  async getStorageReport() {
    const storageStatus = await this.getStorageStatus();
    const filesInfo = await getStoredFilesInfo();

    return {
      ...storageStatus,
      files: filesInfo,
      recommendations: this.getRecommendations(storageStatus, filesInfo),
    };
  }

  // Get storage recommendations
  getRecommendations(storageStatus, filesInfo) {
    const recommendations = [];

    if (storageStatus.usagePercent > STORAGE_WARNING_THRESHOLD) {
      recommendations.push("Consider uploading pending files to free up space");
    }

    if (filesInfo.count > 50) {
      recommendations.push("You have many stored files - consider cleanup");
    }

    const oldFileCount = filesInfo.files.filter((file) => {
      const daysSinceAccess =
        (new Date() - new Date(file.lastAccessed)) / (1000 * 60 * 60 * 24);
      return daysSinceAccess > 7;
    }).length;

    if (oldFileCount > 0) {
      recommendations.push(
        `${oldFileCount} files haven't been accessed in over 7 days`
      );
    }

    return recommendations;
  }

  // Clean up all files for uploaded routes
  async cleanupUploadedRoutes(uploadedRouteIds) {
    let totalDeleted = 0;

    for (const routeId of uploadedRouteIds) {
      const result = await cleanupUploadedFiles(routeId);
      totalDeleted += result.deletedFiles || 0;
    }

    console.log(
      `Cleaned up ${totalDeleted} files from ${uploadedRouteIds.length} uploaded routes`
    );
    return totalDeleted;
  }

  // Schedule periodic cleanup
  startPeriodicCleanup(intervalMinutes = 60) {
    setInterval(async () => {
      try {
        await this.performAutoCleanup();
      } catch (error) {
        console.error("Periodic cleanup failed:", error);
      }
    }, intervalMinutes * 60 * 1000);

    console.log(`Started periodic cleanup every ${intervalMinutes} minutes`);
  }

  // Format bytes for display
  static formatBytes(bytes) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }
}

// Create singleton instance
export const storageManager = new StorageManager();

// Auto-start periodic cleanup when module loads
if (typeof window !== "undefined") {
  // Start cleanup after 5 minutes, then every hour
  const cleanupStartTimer = setTimeout(() => {
    storageManager.startPeriodicCleanup(60);
  }, 5 * 60 * 1000);
  // No-op in browsers; in node-based tests it releases the handle so the
  // test process can exit instead of waiting out the 5 minutes.
  cleanupStartTimer.unref?.();
}
